import React, { useEffect } from 'react';
import { Box, Card, Typography, Grid, Divider, Button, LinearProgress, Tooltip, Breadcrumbs } from '@mui/material';
import { 
    ArrowBack as ArrowBackIcon, 
    Toll as TollIcon,
    Business as BusinessIcon,
    WorkspacePremium as WorkspacePremiumIcon,
    ShoppingCartOutlined as ShoppingCartOutlinedIcon,
    LockOutlined as LockOutlinedIcon,
    Today as TodayIcon,
    DateRange as DateRangeIcon,
    DataUsage as DataUsageIcon,
    EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getServicesIaBalanceByIdStore } from 'store/services-ia-balance/thunks';
import { PageLayout } from 'components/generics/PageLayout';
import { paddingCardsTables } from 'commons/values';
import { formatCredits, formatCreditConsumption, formatDateToYmd } from 'commons/utils/helpers';

export const DetailBalanceIa = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const { balance_active } = useSelector((state: any) => state.servicesIaBalance);

    useEffect(() => {
        if (id) {
            dispatch(getServicesIaBalanceByIdStore(id));
        }
    }, [id, dispatch]);

    const handleBack = () => {
        navigate('/manager/plataforma-ia/creditos/balances');
    };

    if (!balance_active) return null;

    return (
        <Box>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                    Detalle del Plan
                </Typography>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" to="/manager/plataforma-ia/creditos/balance" >
                        Creditos
                    </Link>
                    <Typography color="text.primary">Balances</Typography>
                </Breadcrumbs>
            </Box>
            <Card sx={{ mt: paddingCardsTables, p: 4, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                    Información del Balance
                </Typography>
                <Box sx={{ mb: 4, p: 3, borderRadius: 3, background: 'linear-gradient(135deg, rgba(107, 76, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)', border: '1px solid rgba(107, 76, 255, 0.1)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Disponibilidad de Créditos
                        </Typography>
                        <Tooltip title={`Saldo exacto: ${balance_active.saldoDisponible}`} placement="top">
                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <TollIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.9 }} />
                                {formatCredits(balance_active.saldoDisponible)} / {formatCredits(balance_active.creditosIniciales)} créditos
                            </Typography>
                        </Tooltip>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={Math.min(Math.max((Number(balance_active.saldoDisponible) / (Number(balance_active.creditosIniciales) || 1)) * 100, 0), 100) || 0} 
                        sx={{ 
                            height: 14, 
                            borderRadius: 7, 
                            backgroundColor: 'rgba(0,0,0,0.06)',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 7,
                            }
                        }} 
                        color={
                            (Number(balance_active.saldoDisponible) / (Number(balance_active.creditosIniciales) || 1)) > 0.4 ? 'primary' : 
                            (Number(balance_active.saldoDisponible) / (Number(balance_active.creditosIniciales) || 1)) > 0.15 ? 'warning' : 'error'
                        }
                    />
                </Box>
                
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: 'primary.light' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(107, 76, 255, 0.08)', color: 'primary.main', display: 'flex' }}>
                                <BusinessIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Cliente</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    {balance_active.nombreCliente || 'No definido'}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: 'primary.light' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(107, 76, 255, 0.08)', color: 'primary.main', display: 'flex' }}>
                                <WorkspacePremiumIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Suscripción (Plan)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    {balance_active.plan || 'No definido'}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: 'primary.light' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(107, 76, 255, 0.08)', color: 'primary.main', display: 'flex' }}>
                                <ShoppingCartOutlinedIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Créditos Comprados</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    {formatCredits(balance_active.creditosIniciales)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: 'success.light' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', color: 'success.main', display: 'flex' }}>
                                <TollIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Créditos Disponibles</Typography>
                                <Tooltip title={`Saldo exacto: ${balance_active.saldoDisponible}`} placement="top-start">
                                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, color: 'success.main', display: 'flex', alignItems: 'center', width: 'fit-content', cursor: 'pointer' }}>
                                        {formatCredits(balance_active.saldoDisponible)}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: 'warning.light' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', color: 'warning.main', display: 'flex' }}>
                                <LockOutlinedIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Créditos Reservados</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, color: 'warning.main' }}>
                                    {formatCredits(balance_active.creditosReservados)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', color: 'text.secondary', display: 'flex' }}>
                                <TodayIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Consumido Hoy</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    {formatCreditConsumption(balance_active.consumoHoy)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', color: 'text.secondary', display: 'flex' }}>
                                <DateRangeIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Consumido este Mes</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    {formatCreditConsumption(balance_active.consumoDelMes)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: 'error.light' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(244, 67, 54, 0.1)', color: 'error.main', display: 'flex' }}>
                                <DataUsageIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Consumo Total</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, color: 'error.main' }}>
                                    {formatCreditConsumption(balance_active.consumoTotal)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', color: 'text.secondary', display: 'flex' }}>
                                <EventAvailableIcon />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Fecha de Vencimiento</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    { balance_active.fechaVencimiento ? formatDateToYmd(balance_active.fechaVencimiento) : 'No definida'}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Card>
        </Box>
    );
};
